import { getSession } from '../../auth/session-store';
import { apiDelete, apiGet, apiPost, apiPut, apiUpload } from '../client';
import {
  CustomerFileResponseSchema,
  CustomerNoteResponseSchema,
  CustomerProfileListSchema,
  CustomerProfileResponseSchema,
  EmptyResponseSchema,
  type CustomerFile,
  type CustomerLevel,
  type CustomerNote,
  type CustomerPause,
  type CustomerProfile,
} from '../schemas';

/**
 * The team's own ficha on each client. Internal to the business: the customer
 * never sees any of it, and every read and write is scoped by the session.
 */

function customerPath(customerId: string): string {
  return `/v1/business/customers/${encodeURIComponent(customerId)}`;
}

/** Every client with a level, a tag, or a note: one read that feeds rosters and lists. */
export async function listCustomerProfiles(): Promise<CustomerProfile[]> {
  const response = await apiGet('/v1/business/customer-profiles', CustomerProfileListSchema);
  return response.data;
}

export async function getCustomerFile(customerId: string): Promise<CustomerFile> {
  const response = await apiGet(`${customerPath(customerId)}/file`, CustomerFileResponseSchema);
  return response.data;
}

/** The ficha at a glance: each key is optional on a save, absent keeps it, null clears it. */
export type CustomerDetailsPatch = {
  injuries?: string | null;
  music?: string | null;
  favoriteStaffId?: string | null;
  pause?: CustomerPause | null;
};

/**
 * Level and tags go on every save; a detail goes only when it changed.
 * `expectedVersion` is the version of the ficha the screen read (0 when the client had none);
 * a stale one comes back 409 version_conflict.
 */
export type CustomerProfileInput = CustomerDetailsPatch & {
  level: CustomerLevel | null;
  tags: string[];
  expectedVersion?: number;
};

export async function putCustomerProfile(
  customerId: string,
  input: CustomerProfileInput,
): Promise<CustomerProfile> {
  const response = await apiPut(`${customerPath(customerId)}/profile`, CustomerProfileResponseSchema, input);
  return response.data;
}

export type NotePhotoUpload = { blob: Blob; width: number; height: number };

export async function createCustomerNote(
  customerId: string,
  input: { body: string; photo?: NotePhotoUpload },
  idempotencyKey: string,
): Promise<CustomerNote> {
  const path = `${customerPath(customerId)}/notes`;
  if (!input.photo) {
    const response = await apiPost(path, CustomerNoteResponseSchema, { body: input.body }, idempotencyKey);
    return response.data;
  }
  const form = new FormData();
  form.append('body', input.body);
  form.append('image', input.photo.blob, 'nota.jpg');
  form.append('meta', JSON.stringify({ width: input.photo.width, height: input.photo.height }));
  const response = await apiUpload(path, CustomerNoteResponseSchema, form, idempotencyKey);
  return response.data;
}

export function deleteCustomerNote(
  customerId: string,
  noteId: string,
  idempotencyKey: string,
): Promise<undefined> {
  return apiDelete(`${customerPath(customerId)}/notes/${encodeURIComponent(noteId)}`, EmptyResponseSchema, idempotencyKey);
}

const NOTE_PHOTO_PATH = /^\/v1\/business\/customers\/[A-Za-z0-9_-]+\/notes\/[A-Za-z0-9_-]+\/photo$/;

/**
 * Note photos are private, so they come through the authenticated route rather
 * than the public CDN. A bearer session cannot ride on an `<img src>`, so the
 * bytes are fetched here and handed back as a Blob for an object URL.
 */
export async function fetchNotePhoto(url: string): Promise<Blob> {
  // The bearer token never leaves for a URL the API did not mint for this route.
  if (!NOTE_PHOTO_PATH.test(url)) throw new Error('unexpected note photo url');
  const session = getSession();
  const response = await fetch(url, {
    credentials: 'include',
    headers: session?.transport === 'bearer' && session.token ? { Authorization: `Bearer ${session.token}` } : {},
  });
  if (!response.ok) throw new Error(`note photo ${response.status}`);
  return response.blob();
}
